import { RadioProps, Stack, Text, useRadioGroup } from '@chakra-ui/react';
import { stringToBoolean } from '@pelicargo/utils';
import { useFormContext } from 'react-hook-form';

import { SimpleFieldVariant } from '../utils/types';
import { Radio } from './Radio';
import { SimpleField } from './SimpleField';

type Option = {
  label: string;
  value: string | boolean;
};

type SimpleSwitchProps = RadioProps & {
  name: string;
  label?: string;
  direction?: 'row' | 'column';
  options: Option[];
  variant?: 'inline' | 'outline';
  fieldVariant?: SimpleFieldVariant;
};

export const SimpleSwitch = ({
  name,
  label,
  options,
  fieldVariant,
  variant,
  direction = 'row',
  ...rest
}: SimpleSwitchProps) => {
  const { setValue, watch } = useFormContext();
  const value = watch(name);

  const handleChange = (value: string) => {
    const parsedValue = stringToBoolean(value) ?? value;
    setValue(name, parsedValue);
  };

  const { getRootProps, getRadioProps } = useRadioGroup({
    name,
    value,
    onChange: handleChange,
  });

  const renderRadio = (opt: Option) => {
    const radioProps = getRadioProps({ value: opt.value });
    return (
      <Radio
        key={String(opt.value)}
        variant={variant}
        w="full"
        isVertical={direction === 'column'}
        tabIndex={0}
        _focus={{
          outline: 'none',
          boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6)',
          borderColor: 'blue.500',
        }}
        {...radioProps}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleChange(String(opt.value));
        }}
      >
        <Text fontSize="md" fontWeight="semibold" whiteSpace="nowrap">
          {opt.label}
        </Text>
      </Radio>
    );
  };

  return (
    <SimpleField name={name} label={label} variant={fieldVariant}>
      <Stack direction={direction} h="full" w="full" bg="white" borderRadius="md" {...rest} {...getRootProps()}>
        {options.map(renderRadio)}
      </Stack>
    </SimpleField>
  );
};
